import React, {useState, useEffect, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import wrap from '../util/wrap_with_intl_provider';
import Table from '../../../../components/Table';
import ax from '../util/axios';
import { tableParamsBuilder } from '../util/searchUtils';
import UsersRowMenu from './UsersRowMenu';
import SearchBar from '../../../../components/SearchBar';
import { PrimarySearchItems, SecondarySearchItems } from './UserSearchForm';
import {
  edit_admin_area_organisation_user_path,
  edit_organisation_area_settings_user_path,
  admin_area_student_path,
  organisation_area_student_path,
  admin_area_settings_user_send_password_path,
  organisation_area_settings_user_send_password_path,
  admin_area_settings_user_revoke_org_admin_path,
  organisation_area_settings_user_revoke_org_admin_path,
  admin_area_settings_user_path,
  organisation_area_settings_user_path,
  admin_area_settings_user_revoke_admin_path,
  admin_area_settings_user_grant_admin_path,
  organisation_area_settings_user_grant_org_admin_path,
  admin_area_settings_user_grant_org_admin_path,
} from '../../../../routes';

const UserTable = (props) => {
  const intl = useIntl();
  const [pagination, setPagination] = useState({});
  const [users, setUsers] = useState([]);
  const [searchParams, setSearchParams] = useState(props.forOrgUser ? {orgAdmin: 'true'} : { admin: 'true'});

  const fetchUsers = async (page = 1) => {
    const response = await ax.get(props.usersUrl + '.json' + tableParamsBuilder({ page, ...searchParams }));
    setPagination(response.data.pagination);
    setUsers(response.data.users);
  };

  const userColumns = () => {
    const columns = [
      {
        accessor: 'email',
        header: intl.formatMessage({ id: 'users.search.email-field' }),
      },
      {
        accessor: 'firstName',
        header: intl.formatMessage({ id: 'users.search.first-name-field' }),
      },
      {
        accessor: 'lastName',
        header: intl.formatMessage({ id: 'users.search.last-name-field' }),
      },
      {
        accessor: 'lastLogin',
        header: intl.formatMessage({ id: 'users.search.last-login-field' }),
      },
      {
        accessor: 'role',
        className: 'status',
        header: intl.formatMessage({ id: 'users.search.role-field' }),
      },
      {
        className: 'actions',
        accessor: 'rowMenu',
      },
    ];

    if (props.forOrgUser) {
      columns.splice(4, 0, { accessor: 'mobile', header: intl.formatMessage({ id: 'students.fields.mobile' }) });
    }

    return columns;
  };

  const renderRowMenu = (user) => {
    if (props.forOrgUser) {
      return (
        <UsersRowMenu
          editUserUrl={
            props.isAdmin
              ? edit_admin_area_organisation_user_path(user.organisationId, user.id)
              : edit_organisation_area_settings_user_path(user.id)
          }
          viewStudentUrl={
            user.studentId &&
              (props.isAdmin ? admin_area_student_path(user.id) : organisation_area_student_path(user.id))
          }
          sendPasswordUrl={
            props.isAdmin
              ? admin_area_settings_user_send_password_path({ user_id: user.id })
              : organisation_area_settings_user_send_password_path({ user_id: user.id })
          }
          grantOrgAdminUrl={
            user.org_admin ? '' : grantOrgAdminUrl(props, user)
          }
          revokeOrgAdminUrl={
            user.org_admin ? revokeOrgAdminUrl(props, user) : ''
          }
        />
      );
    } else {
      return (
        <UsersRowMenu
          updateUserUrl={
            props.isAdmin ? admin_area_settings_user_path(user.id) : organisation_area_settings_user_path(user.id)
          }
          sendPasswordUrl={
            props.isAdmin
              ? admin_area_settings_user_send_password_path({ user_id: user.id })
              : organisation_area_settings_user_send_password_path({ user_id: user.id })
          }
          grantAdminUrl={
            props.isAdmin && user.admin === false
              ? admin_area_settings_user_grant_admin_path({ user_id: user.id })
              : ''
          }
          revokeAdminUrl={
            props.isAdmin && user.admin === true
              ? admin_area_settings_user_revoke_admin_path({ user_id: user.id })
              : ''
          }
          userDetails={{
            first_name: user.firstName,
            last_name: user.lastName,
            email: user.email,
          }}
        />
      );
    }
  };

  const grantOrgAdminUrl = (props, user) => {
    return props.isAdmin
      ? admin_area_settings_user_grant_org_admin_path({ user_id: user.id })
      : organisation_area_settings_user_grant_org_admin_path({ user_id: user.id })
  }

  const revokeOrgAdminUrl = (props, user) => {
    return props.isAdmin
      ? admin_area_settings_user_revoke_org_admin_path({ user_id: user.id })
      : organisation_area_settings_user_revoke_org_admin_path({ user_id: user.id })
  }

  const role = (props, user) => {
    if (props.forOrgUser) {
      return user.org_admin ? 'users.roles.org-admin' : 'users.roles.student'
    } else {
      return user.admin ? 'users.roles.admin' : 'users.roles.user'
    }
  }

  const mappedUsers = users.map(user => {
    return {
      ...user,
      role: intl.formatMessage({ id: role(props, user) }),
      rowMenu: renderRowMenu(user)
    }
  })

  useEffect(() => {
    fetchUsers();
  }, [searchParams]);

  return (
    <div>
      <SearchBar
        primaryItems={<PrimarySearchItems forOrgUser={props.forOrgUser}/>}
        secondaryItems={<SecondarySearchItems />}
        onSearch={setSearchParams}
      />
      {users.length > 0 && (
        <Table
          header={true}
          items={mappedUsers}
          columns={userColumns()}
          pagination={pagination}
          onPageChange={fetchUsers}
        />
      )}
    </div>
  );
};

UserTable.propTypes = {
  usersUrl: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  forOrgUser: PropTypes.bool.isRequired,
};

export default wrap(UserTable);